import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { X, UserPlus, Shuffle } from 'lucide-react';

const BulkAssignModal = ({ selectedLeadIds, onClose }) => {
  const [callers, setCallers] = useState([]);
  const [selectedCaller, setSelectedCaller] = useState('');
  const [loading, setLoading] = useState(false);
  const [randomize, setRandomize] = useState(false);
  const [selectedCallers, setSelectedCallers] = useState([]);

  useEffect(() => {
    fetchCallers();
  }, []);

  const fetchCallers = async () => {
    try {
      const response = await api.getUsers('caller');
      setCallers(response.data);
    } catch (error) {
      toast.error('Failed to fetch callers');
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    
    if (randomize) {
      if (selectedCallers.length === 0) {
        toast.error('Please select at least one caller for random assignment');
        return;
      }
    } else {
      if (!selectedCaller) {
        toast.error('Please select a caller');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = randomize
        ? {
            lead_ids: selectedLeadIds,
            caller_ids: selectedCallers,
            randomize: true
          }
        : {
            lead_ids: selectedLeadIds,
            caller_id: selectedCaller
          };

      const response = await api.assignLeads(payload);
      
      if (randomize && response.data.distribution) {
        toast.success(`${response.data.message}. Distribution: ${response.data.distribution}`);
      } else {
        toast.success(response.data.message);
      }
      
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign leads');
    } finally {
      setLoading(false);
    }
  };

  const handleCallerToggle = (callerId) => {
    setSelectedCallers(prev =>
      prev.includes(callerId)
        ? prev.filter(id => id !== callerId)
        : [...prev, callerId]
    );
  };

  const handleSelectAllCallers = () => {
    if (selectedCallers.length === callers.length) {
      setSelectedCallers([]);
    } else {
      setSelectedCallers(callers.map(c => c.id));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-headings text-slate-900">Assign Leads</h2>
            <p className="text-slate-600 font-body text-sm mt-1">
              Assigning {selectedLeadIds.length} lead{selectedLeadIds.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            data-testid="close-assign-modal"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X size={24} className="text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleAssign} className="p-6 space-y-6">
          {/* Randomizer Toggle */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="randomize-assign-checkbox"
                checked={randomize}
                onChange={(e) => {
                  setRandomize(e.target.checked);
                  if (e.target.checked) {
                    setSelectedCaller('');
                  } else {
                    setSelectedCallers([]);
                  }
                }}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-semibold text-slate-900 font-body mb-1">
                  <Shuffle size={18} className="text-blue-600" />
                  <span>Randomly distribute leads</span>
                </div>
                <p className="text-sm text-slate-600 font-body">
                  Automatically distribute leads evenly among selected callers
                </p>
              </div>
            </label>
          </div>

          {/* Single Caller Selection */}
          {!randomize && (
            <div>
              <label htmlFor="caller" className="block text-sm font-medium text-slate-700 font-body mb-2">
                Select Caller
              </label>
              <select
                id="caller"
                data-testid="caller-select"
                value={selectedCaller}
                onChange={(e) => setSelectedCaller(e.target.value)}
                required={!randomize}
                className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
              >
                <option value="">Choose a caller...</option>
                {callers.map((caller) => (
                  <option key={caller.id} value={caller.id}>
                    {caller.name} ({caller.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiple Caller Selection */}
          {randomize && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700 font-body">
                  Select Callers for Random Assignment
                </label>
                <button
                  type="button"
                  data-testid="select-all-callers-assign"
                  onClick={handleSelectAllCallers}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedCallers.length === callers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2 bg-slate-50 rounded-md p-3 border border-slate-200">
                {callers.length === 0 ? (
                  <p className="text-sm text-slate-500 font-body text-center py-2">No callers available</p>
                ) : (
                  callers.map((caller) => (
                    <label
                      key={caller.id}
                      data-testid={`caller-checkbox-assign-${caller.id}`}
                      className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCallers.includes(caller.id)}
                        onChange={() => handleCallerToggle(caller.id)}
                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      />
                      <span className="text-sm font-body text-slate-700">{caller.name}</span>
                      <span className="text-xs text-slate-500 font-body">({caller.email})</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {callers.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-body">
                No callers available. Please create caller accounts first.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 transition-all shadow-sm font-body"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="assign-submit-button"
              disabled={loading || (!randomize && !selectedCaller) || (randomize && selectedCallers.length === 0)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus size={18} />
              <span>{loading ? 'Assigning...' : 'Assign Leads'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkAssignModal;